"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TIER_LABEL = exports.TIER_LADDER = exports.TIER_RATE_TYPE = exports.WARD_TIERS = void 0;
exports.tierForRateType = tierForRateType;
exports.tierFallbackChain = tierFallbackChain;
exports.recommendedPricePaise = recommendedPricePaise;
exports.actualMarginPercent = actualMarginPercent;
exports.roundPercent = roundPercent;
exports.applyAutoCalculation = applyAutoCalculation;
exports.resetToAutoCalculation = resetToAutoCalculation;
exports.validateTierPricing = validateTierPricing;
exports.marginBand = marginBand;
exports.validatePayerRates = validatePayerRates;
exports.validateChargePricing = validateChargePricing;
/** The three that are optional and derived from C1. C1 is never in this set. */
exports.WARD_TIERS = ['C2', 'C3', 'C4'];
/**
 * Tier → rate type. C1 maps to the pre-existing unconditional `STANDARD`, which
 * is why an OP bill — which supplies no ward context at all — lands on C1
 * without a single OP-specific branch anywhere in the resolver.
 */
exports.TIER_RATE_TYPE = {
    C1: 'STANDARD',
    C2: 'SEMI_PRIVATE',
    C3: 'PRIVATE',
    C4: 'DELUXE',
};
/**
 * THE TIER A LINE ACTUALLY RESOLVED AT — the reverse of `TIER_RATE_TYPE`.
 *
 * Added 2026-08-31 with the Category column on the billing grid. The column
 * cannot simply repeat the category the operator chose, because a line does not
 * always get it: `tierFallbackChain` walks DOWN the ladder when a charge has no
 * price at the asserted tier, so a C3 bill can contain a line priced at C2. A
 * column that showed "C3" on that line would be stating something the ledger
 * disagrees with, on the screen whose whole job is to say what is being charged.
 *
 * Derived from the priced line's `rateType`, which the server already sends and
 * which IS the applied answer. Returns null for a rate type that is not one of
 * the four tiers at all — a corporate tariff or a package allowance is not a
 * tier, and guessing one for it would invent a category nobody selected.
 */
function tierForRateType(rateType) {
    if (!rateType)
        return null;
    const found = exports.TIER_LADDER.find((tier) => exports.TIER_RATE_TYPE[tier] === rateType);
    return found ?? null;
}
/**
 * THE TIER LADDER, cheapest first. C1 is the floor every other tier stands on.
 *
 * Written once, here, because two things depend on the ORDER and would drift
 * apart if each restated it: the pricing screen derives C2/C3/C4 upward from
 * C1, and billing walks the same rungs DOWNWARD when a charge has no price at
 * the tier a ward asserted.
 */
exports.TIER_LADDER = ['C1', 'C2', 'C3', 'C4'];
/**
 * Where a tier's price comes from when the tier itself has none — the next rung
 * DOWN, then the next, ending at C1.
 *
 * Desk instruction (2026-08-26): "if value not available in that tier should go
 * before category. if C3 selected value not available should select C2".
 *
 * The rule this replaces dropped straight from any tier to C1, which quietly
 * charged a Deluxe bed the General rate whenever the Deluxe price was missing —
 * skipping over a configured Private price that was the far better answer. A
 * ward's own tier is the first choice; the closest one below it is the next
 * honest one; C1 is the floor rather than the default.
 *
 * Returns tiers only, in the order they should be tried. C1 returns [] — there
 * is nothing below it, and a missing C1 is a configuration failure to be shouted
 * about rather than papered over.
 */
function tierFallbackChain(tier) {
    const index = exports.TIER_LADDER.indexOf(tier);
    if (index <= 0)
        return [];
    return exports.TIER_LADDER.slice(0, index).reverse();
}
/** What the operator is shown. The hospital's words, not the enum's. */
exports.TIER_LABEL = {
    C1: 'General',
    C2: 'Semi Private',
    C3: 'Private',
    C4: 'Deluxe',
};
// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------
/**
 * C1 plus a margin, rounded to the nearest paise.
 *
 * Rounding at paise rather than at rupees is deliberate: the brief's own worked
 * example is 110.00 → 126.50 at +15%, which does not survive rupee rounding. A
 * hospital that wants whole-rupee tier prices gets them by choosing margins that
 * produce them, or by typing the price — which is exactly what MANUAL is for.
 */
function recommendedPricePaise(basePricePaise, marginPercent) {
    return Math.round(basePricePaise * (1 + marginPercent / 100));
}
/**
 * How far a price actually sits above C1, as a percent.
 *
 * Returns null when C1 is zero: the question "what percent above zero" has no
 * answer, and returning 0 or Infinity would both be read as a real measurement
 * by the caller. Null forces the caller to say something honest instead.
 */
function actualMarginPercent(basePricePaise, pricePaise) {
    if (basePricePaise === 0)
        return null;
    return ((pricePaise - basePricePaise) / basePricePaise) * 100;
}
/** Percent rounded for display. One decimal — the brief shows "1.6%" and "-8%". */
function roundPercent(value) {
    return Math.round(value * 10) / 10;
}
// ---------------------------------------------------------------------------
// Auto-calculation
// ---------------------------------------------------------------------------
/**
 * Recompute every ENABLED, AUTO tier from C1. Disabled tiers and MANUAL tiers
 * are returned untouched.
 *
 * A disabled tier keeps whatever price it had rather than being cleared, so
 * un-ticking and re-ticking a tier does not silently discard a price the
 * operator typed. Nothing reads a disabled tier's price — `toPriceVersions`
 * skips it — so retaining it costs nothing and losing it costs data entry.
 */
function applyAutoCalculation(state) {
    const next = {};
    for (const tier of exports.WARD_TIERS) {
        const current = state.tiers[tier];
        const shouldDerive = current.enabled && current.mode === 'AUTO';
        next[tier] = shouldDerive
            ? { ...current, pricePaise: recommendedPricePaise(state.basePricePaise, state.margins[tier]) }
            : { ...current };
    }
    return next;
}
/**
 * The mockup's [Auto Calculate] button: force every enabled tier back onto C1,
 * discarding MANUAL overrides.
 *
 * Separate from `applyAutoCalculation` rather than a boolean flag on it, because
 * these are different events with different consent. One is a side effect of
 * editing C1 and must preserve typed prices; the other is an explicit request to
 * throw them away. A single function with a `force` parameter would make the
 * destructive case reachable by passing the wrong argument.
 */
function resetToAutoCalculation(state) {
    const reset = {};
    for (const tier of exports.WARD_TIERS) {
        reset[tier] = { ...state.tiers[tier], mode: 'AUTO' };
    }
    return applyAutoCalculation({ ...state, tiers: reset });
}
// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
/**
 * Every tier finding. Warnings only — this function cannot produce an ERROR,
 * and that is a property worth stating out loud because it is the brief's
 * central UX rule ("Never block. Only warn.").
 *
 * Order of checks per tier matters. "Below C1" is reported INSTEAD of "below
 * recommended margin", not in addition to it: a price under General is already
 * under any positive margin, and emitting both would put two dialogs in front of
 * an operator for one mistake, which trains them to dismiss both.
 */
function validateTierPricing(state) {
    const findings = [];
    for (const tier of exports.WARD_TIERS) {
        const { enabled, pricePaise } = state.tiers[tier];
        if (!enabled || pricePaise === null)
            continue;
        const label = exports.TIER_LABEL[tier];
        const recommended = state.margins[tier];
        const actual = actualMarginPercent(state.basePricePaise, pricePaise);
        // C1 of zero makes every percentage meaningless. Say so once, plainly,
        // rather than emitting a margin warning computed from a division we refused
        // to perform.
        if (actual === null) {
            if (pricePaise > 0) {
                findings.push({
                    severity: 'WARNING',
                    code: 'TIER_ABOVE_ZERO_BASE',
                    tier,
                    message: `${label} is priced but General is zero, so no margin can be checked.`,
                });
            }
            continue;
        }
        if (pricePaise < state.basePricePaise) {
            findings.push({
                severity: 'WARNING',
                code: 'TIER_BELOW_BASE',
                tier,
                message: `${label} tariff is lower than General.`,
                actualPercent: roundPercent(actual),
                recommendedPercent: recommended,
            });
            continue;
        }
        if (actual + EPSILON_PERCENT < recommended) {
            findings.push({
                severity: 'WARNING',
                code: 'TIER_BELOW_RECOMMENDED_MARGIN',
                tier,
                message: `${label} price is only ${roundPercent(actual)}% higher than General. Recommended minimum ${recommended}%.`,
                actualPercent: roundPercent(actual),
                recommendedPercent: recommended,
            });
        }
    }
    return findings;
}
/**
 * Floating-point slack for the margin comparison.
 *
 * `recommendedPricePaise` rounds to whole paise, so the auto-calculated price
 * frequently lands a few ten-thousandths of a percent under its own target
 * (110.00 at +15% is exactly 126.50, but 111.00 at +15% is 127.65 against a
 * target of 127.6500000000000057). Without this slack the engine would warn
 * about prices IT generated, which is the fastest way to teach operators that
 * the warnings are noise.
 */
const EPSILON_PERCENT = 0.01;
/**
 * `CAUTION` starts at two-thirds of the recommended margin.
 *
 * Derived from the brief's own worked example against a 15% recommendation:
 * 12% must read amber (12/15 = 0.80) and 8% must read red (8/15 = 0.53). Any
 * cut between 0.54 and 0.79 satisfies both; two-thirds is the roundest, and
 * being a RATIO rather than a fixed percentage means it still behaves when a
 * hospital moves its recommended margin to 35%.
 */
const CAUTION_RATIO = 2 / 3;
function marginBand(basePricePaise, pricePaise, recommendedPercent) {
    // Below the base price is never anything but red, whatever the ratio says —
    // a ward tier cheaper than General is a data-entry error far more often than
    // it is a commercial decision.
    if (pricePaise < basePricePaise)
        return 'RISK';
    const actual = actualMarginPercent(basePricePaise, pricePaise);
    // No base to measure against. Not good, not risky — simply unknowable, and
    // GOOD would paint an unverified price green.
    if (actual === null)
        return 'CAUTION';
    // A zero recommendation means the hospital asks for no uplift, so any price
    // at or above base satisfies it. Without this, 0 * 2/3 = 0 makes every tier
    // GOOD by accident rather than by rule.
    if (recommendedPercent <= 0)
        return 'GOOD';
    if (actual + EPSILON_PERCENT >= recommendedPercent)
        return 'GOOD';
    if (actual >= recommendedPercent * CAUTION_RATIO)
        return 'CAUTION';
    return 'RISK';
}
/**
 * CGHS and corporate rows. These CAN produce errors — "if applicable, price
 * mandatory" is the brief's own rule, and an applicable payer with no price is
 * incomplete data rather than an unusual commercial position.
 *
 * A corporate rate ABOVE General is explicitly not a finding of any kind. The
 * brief calls this out and it is correct: hospitals routinely hold corporate
 * rates above list, and warning about it would be the system second-guessing a
 * signed contract.
 */
function validatePayerRates(payers) {
    const findings = [];
    for (const payer of payers) {
        if (!payer.applicable)
            continue;
        if (payer.pricePaise === null) {
            findings.push({
                severity: 'ERROR',
                code: 'PAYER_PRICE_REQUIRED',
                message: `${payer.payerName} price is required when ${payer.payerName} is marked applicable.`,
            });
        }
    }
    return findings;
}
function validateChargePricing(state, payers) {
    const findings = [...validateTierPricing(state), ...validatePayerRates(payers)];
    const errors = findings.filter((f) => f.severity === 'ERROR');
    const warnings = findings.filter((f) => f.severity === 'WARNING');
    return {
        findings,
        errors,
        warnings,
        canSave: errors.length === 0,
        requiresConfirmation: warnings.length > 0,
    };
}
//# sourceMappingURL=charge-tier-pricing.js.map